<template>
  <div class="flex justify-center items-center px-4 py-10 min-h-screen bg-brand-light-blue">
    <!-- Card: 504px on desktop, full width (328px at the 360px design frame) on mobile -->
    <div class="px-3 py-6 w-full max-w-[504px] bg-white rounded-3xl lg:p-6">
      <form class="flex flex-col gap-[39px]" novalidate @submit.prevent="onSubmit">
        <div class="flex flex-col gap-3 text-brand-dark-blue">
          <h1 class="font-poppins text-28 font-semibold leading-9">
            {{ $t('login.title') }}
          </h1>
          <p class="text-15 leading-[1.7]">
            {{ $t('login.subtitle') }}
          </p>
        </div>

        <div class="flex flex-col gap-4">
          <UFormField :label="$t('login.email')" :error="errors.email">
            <UInput
                v-model="email"
                variant="default"
                :ui="{ base: 'text-15 md:text-15 leading-4.5' }"
                type="email"
                name="email"
                autocomplete="email"
                :disabled="loading"
            />
          </UFormField>

          <UFormField :label="$t('login.password')" :error="errors.password">
            <UInput
                v-model="password"
                variant="default"
                :ui="{ base: 'text-15 md:text-15 leading-4.5' }"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                autocomplete="current-password"
                :disabled="loading"
            >
              <template #trailing>
                <UButton
                    variant="link"
                    color="neutral"
                    size="sm"
                    :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    :aria-label="showPassword ? $t('account.password.hidePassword') : $t('account.password.showPassword')"
                    :aria-pressed="showPassword"
                    tabindex="-1"
                    @click="showPassword = !showPassword"
                />
              </template>
            </UInput>
          </UFormField>

          <p v-if="serverError" class="px-4 py-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-200" role="alert">
            {{ serverError }}
          </p>

          <p v-else-if="sessionExpired" class="px-4 py-3 text-sm rounded-lg border text-brand-dark-blue bg-brand-light-blue border-brand-dark-grey-blue" role="status">
            {{ $t('login.expired') }}
          </p>
        </div>

        <div class="flex flex-col gap-3.5">
          <Button type="submit" class="w-full" :disabled="loading">
            {{ loading ? $t('login.submitting') : $t('login.submit') }}
          </Button>
          <NuxtLinkLocale
              :to="{ name: 'remind-password' }"
              class="text-xs font-medium leading-none text-brand-dark-blue/70 transition-opacity hover:opacity-100"
          >
            {{ $t('login.forgotPassword') }}
          </NuxtLinkLocale>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'

definePageMeta({ middleware: 'guest' })
defineRouteRules({ cache: false })

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
// Captured at setup: `navigateTo` loses the Nuxt app context across the `await`
// inside vee-validate's submit handler and silently resolves without navigating.
const router = useRouter()
const store = useCustomerStore()

const loading = ref(false)
const showPassword = ref(false)
const serverError = ref('')

// `?expired=1` is set by `middleware/gate.global.ts` (and by `error.vue`'s 401
// fallback) when a session was ended for the customer rather than by them, so the
// login screen explains itself instead of looking like an unexplained logout.
// Cleared on the first submit: after that the form's own result is the news.
const sessionExpired = ref(route.query.expired === '1')

const schema = z.object({
  email: z.string().email(t('errors.email')),
  password: z.string().min(1, t('errors.passwordRequired')),
})

const { handleSubmit, errors, defineField } = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues: { email: '', password: '' },
})

const [email] = defineField('email')
const [password] = defineField('password')

useSeoMeta({ title: () => t('login.title'), robots: 'noindex, nofollow' })

const onSubmit = handleSubmit(async (values) => {
  loading.value = true
  serverError.value = ''
  sessionExpired.value = false

  try {
    await store.login(values.email, values.password)
    await router.push(safeRedirect(route.query.redirect) ?? localePath('/'))
  } catch (err) {
    // 401 is the only expected failure — anything else is the API being down,
    // which must not read as "wrong password".
    const status = (err as { statusCode?: number; status?: number })?.statusCode
      ?? (err as { status?: number })?.status
    serverError.value = status === 401 ? t('login.error') : t('errors.server')
  } finally {
    loading.value = false
  }
})
</script>
